import type { BidType } from '@/features/bidding/constants'
import type { BidVolumes, ExtendedBidPtu } from '@/features/bidding/types/bid'
import { buildDefaultOfferedBid } from '@/features/bidding/utils/calculations/calculateBasisForNewBid'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'

import { findForecast, findLatestBid, findSuggestedBid } from './bids'

export const CALCULATE_BASIS_FOR_NEW_BID_API_ID = 'CALCULATE_BASIS_FOR_NEW_BID'

export const calculateBasisForNewBid = async (
  date: MarketDate,
  activationGroupUuid: string,
  bidType: BidType,
  selectedCountry: CountryIdentifier,
  isValueStackingEnabled: boolean = false,
): Promise<BidVolumes> => {
  const foundBid = await findLatestBid(date, activationGroupUuid, bidType)
  const foundForecast = await findForecast(date, activationGroupUuid)
  // don't make additional request unless country and market program support value stacking
  const foundSuggestedBid = isValueStackingEnabled ? await findSuggestedBid(date, date, activationGroupUuid) : []

  if (foundBid) {
    return {
      ptus: foundBid.ptus,
      forecast: foundForecast ? foundForecast.forecastPtus : undefined,
      suggestedBid: foundSuggestedBid ?? undefined,
    }
  }

  if (foundForecast || foundSuggestedBid?.length > 0) {
    const forecastPtus = foundForecast?.forecastPtus ?? []
    const suggestedBidPtus = foundSuggestedBid ?? []
    const offeredBidPtus = buildDefaultOfferedBid(selectedCountry, date, bidType).map((offerPtu: ExtendedBidPtu) => {
      const forecastPtu = forecastPtus.find((forecastPtu) => forecastPtu.ptu.start.equals(offerPtu.ptu.start))
      const suggestedBidPtu = suggestedBidPtus.find((suggestedBid) => suggestedBid.ptu.start.equals(offerPtu.ptu.start))

      return {
        ptu: offerPtu.ptu,
        offeredVolume: {
          quantity: suggestedBidPtu?.volume.quantity ?? forecastPtu?.volume.quantity ?? offerPtu.offeredVolume.quantity,
          unit: offerPtu.offeredVolume.unit,
        },
        ptuChunks: [],
      }
    })

    return {
      ptus: offeredBidPtus,
      forecast: forecastPtus.length > 0 ? forecastPtus : undefined,
      suggestedBid: suggestedBidPtus.length > 0 ? suggestedBidPtus : undefined,
    }
  }

  return {
    ptus: buildDefaultOfferedBid(selectedCountry, date, bidType),
  }
}
